import { useState } from "react";
import { Star } from "lucide-react";
import { KPI_TIERS } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const medalStyles = {
    gold: {
        color: "var(--color-medal-gold)",
        glow: "0 0 6px rgba(251, 191, 36, 0.4), 0 0 12px rgba(251, 191, 36, 0.2)",
        textShadow: "0 0 6px rgba(251, 191, 36, 0.3), 0 0 10px rgba(251, 191, 36, 0.2)",
    },
    silver: {
        color: "var(--color-medal-silver)",
        glow: "0 0 5px rgba(209, 213, 219, 0.5), 0 0 10px rgba(209, 213, 219, 0.3)",
        textShadow: "0 0 5px rgba(209, 213, 219, 0.4), 0 0 8px rgba(209, 213, 219, 0.3)",
    },
    bronze: {
        color: "var(--color-rank-bronze)",
        glow: "0 0 5px rgba(251, 146, 60, 0.4), 0 0 10px rgba(251, 146, 60, 0.2)",
        textShadow: "0 0 5px rgba(251, 146, 60, 0.4), 0 0 8px rgba(251, 146, 60, 0.3)",
    },
} as const;

const TIER_EXTRAS = [
    { range: "< 40", percentage: 11 },
    { range: "40–50", percentage: 20 },
    { range: "50–60", percentage: 25 },
    { range: "60–70", percentage: 22 },
    { range: "70–80", percentage: 13 },
    { range: "80–90", percentage: 6 },
    { range: "90+", percentage: 3 },
] as const;

const tiers = KPI_TIERS.map((t, i) => ({ ...t, ...TIER_EXTRAS[i] }));

const cumulativeLeft = tiers.reduce<number[]>((acc, _tier, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + tiers[i - 1].percentage);
    return acc;
}, []);

export const KpiScoringSystem = () => {
    const [activeTier, setActiveTier] = useState<number | null>(null);
    const isMobile = useIsMobile();

    return (
        <div className="space-y-3">
            <h3 className="text-base font-headline font-bold text-on-surface">Score Distribution</h3>
            <div className="bg-surface-container rounded-md p-5 border border-outline-variant/20">
                <div className="flex items-center justify-between text-[10px] text-on-surface-variant mb-1.5 px-1">
                    <span>← Lower scores</span>
                    <span>Higher scores →</span>
                </div>

                <div className="relative mb-12 mt-8">
                    {tiers.map((tier, i) => {
                        const centerPct = cumulativeLeft[i] + tier.percentage / 2;
                        return (
                            <div
                                key={`label-${tier.range}`}
                                className="absolute -top-6 -translate-x-1/2 text-[10px] font-semibold transition-opacity duration-200"
                                style={{
                                    left: `${centerPct}%`,
                                    color: tier.color,
                                    opacity: activeTier === null || activeTier === i ? 1 : 0.3,
                                }}
                            >
                                {tier.percentage}%
                            </div>
                        );
                    })}

                    <div className="flex h-10 rounded-full overflow-hidden shadow-lg">
                        {tiers.map((tier, i) => (
                            <div
                                key={tier.range}
                                className="relative flex items-center justify-center cursor-default transition-all duration-200"
                                style={{
                                    width: `${tier.percentage}%`,
                                    backgroundColor: tier.color,
                                    opacity: activeTier === null || activeTier === i ? 1 : 0.3,
                                    filter: activeTier === i ? `drop-shadow(0 0 8px ${tier.color})` : "none",
                                }}
                                onMouseEnter={() => setActiveTier(i)}
                                onMouseLeave={() => setActiveTier(null)}
                            >
                                {activeTier === i && (
                                    <div className="absolute -top-20 left-1/2 -translate-x-1/2 bg-surface-container-lowest border border-outline-variant/20 rounded-md px-3 py-1.5 whitespace-nowrap z-20 shadow-[0_30px_60px_rgba(5,0,102,0.06)] backdrop-blur-sm">
                                        <div className="text-xs text-on-surface font-medium">{tier.label}</div>
                                        <div className="text-[10px] text-on-surface-variant">
                                            {tier.range} pts · {tier.percentage}% of players
                                        </div>
                                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-surface-container-lowest border-r border-b border-outline-variant/20" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div
                        className="absolute top-0 flex flex-col items-center pointer-events-none"
                        style={{ left: `${cumulativeLeft[2] + tiers[2].percentage}%` }}
                    >
                        <div className="w-0.5 h-10 bg-white/80" />
                        <div className="mt-1.5 bg-surface-container border border-outline-variant/20 rounded-full px-2.5 py-0.5">
                            <span className="text-[11px] font-semibold text-on-surface">avg ≈ 60</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-1 sm:gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
                    {tiers.map((tier, i) => (
                        <div
                            key={tier.range}
                            className="relative rounded-lg overflow-hidden transition-all duration-200 cursor-default
                                       flex flex-col px-2.5 py-1.5
                                       sm:text-center sm:gap-0 sm:p-3"
                            style={{
                                background: `linear-gradient(180deg, ${tier.color}40 0%, ${tier.color}18 100%), var(--color-surface-container-lowest)`,
                                border: `1px solid ${!isMobile && activeTier === i ? tier.color + "80" : tier.color + "25"}`,
                                transform: !isMobile && activeTier === i ? "scale(1.05)" : "scale(1)",
                                boxShadow: !isMobile && activeTier === i ? `0 0 16px ${tier.color}20` : "none",
                                opacity: isMobile || activeTier === null || activeTier === i ? 1 : 0.3,
                            }}
                            onMouseEnter={() => !isMobile && setActiveTier(i)}
                            onMouseLeave={() => !isMobile && setActiveTier(null)}
                        >
                            <div
                                className="absolute top-0 left-0 right-0 h-0.5"
                                style={{ backgroundColor: tier.color }}
                            />

                            <div className="flex items-center gap-2 sm:hidden">
                                <div
                                    className="text-sm font-bold tracking-tight shrink-0"
                                    style={{ color: tier.color }}
                                >
                                    {tier.percentage}%
                                </div>
                                <div className="flex items-baseline gap-1.5 min-w-0">
                                    <span className="text-[11px] font-semibold text-on-surface leading-tight">
                                        {tier.label}
                                    </span>
                                    <span className="text-[10px] text-on-surface-variant leading-tight">
                                        {tier.range} pts
                                    </span>
                                </div>
                            </div>

                            {/* Mobile: progress bar */}
                            <div className="mt-1.5 h-1 bg-surface-container-highest rounded-full overflow-hidden relative sm:hidden">
                                <div
                                    className="absolute top-0 h-full rounded-full"
                                    style={{
                                        left: `${tier.from}%`,
                                        width: `${Math.min(tier.to, 100) - tier.from}%`,
                                        backgroundColor: tier.color,
                                        opacity: 0.7,
                                    }}
                                />
                            </div>

                            {tier.medal ? (
                                <div className="hidden sm:flex items-center justify-center mb-1">
                                    <Star
                                        className="w-3.5 h-3.5"
                                        style={{
                                            color: medalStyles[tier.medal].color,
                                            fill: medalStyles[tier.medal].color,
                                            filter: `drop-shadow(${medalStyles[tier.medal].glow.split(",")[0]})`,
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="hidden sm:block mb-1 h-3.5" />
                            )}

                            <div
                                className="hidden sm:block text-xl font-bold tracking-tight"
                                style={{ color: tier.color }}
                            >
                                {tier.percentage}%
                            </div>
                            <div className="hidden sm:block text-[11px] font-semibold text-on-surface mt-1">
                                {tier.label}
                            </div>
                            <div className="hidden sm:block text-[10px] text-on-surface-variant mt-0.5">
                                {tier.range} pts
                            </div>

                            <div className="hidden sm:block mt-2.5 h-1.5 bg-surface-container-highest rounded-full overflow-hidden relative w-full">
                                <div
                                    className="absolute top-0 h-full rounded-full"
                                    style={{
                                        left: `${tier.from}%`,
                                        width: `${Math.min(tier.to, 100) - tier.from}%`,
                                        backgroundColor: tier.color,
                                        opacity: 0.7,
                                    }}
                                />
                            </div>
                            <div className="hidden sm:flex justify-between mt-0.5 text-[8px] text-on-surface-variant">
                                <span>0</span>
                                <span>100</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
